/**
 * Role name presentation.
 *
 * Role names are snake_case identifiers in the database (`hr_admin`) because they are
 * used as lookup keys in code and in the RBAC seeds. They were being rendered verbatim
 * in the UI, which is developer-facing, not user-facing.
 *
 * Labels are derived from the identifier rather than stored as a `display_name` column:
 * admins can create custom roles at runtime, so a derived label means a new role reads
 * correctly the moment it exists - no migration, and no second field to keep in sync.
 */

// Words that must not be title-cased. Naive capitalisation renders `hr_admin` as
// "Hr Admin", which reads as a typo.
const ACRONYMS = new Set(['hr', 'it', 'rbac', 'ceo', 'cto', 'cfo', 'coo', 'qa', 'pm']);

/**
 * Turn a role identifier into a human label.
 *   hr_admin      -> HR Admin
 *   super_admin   -> Super Admin
 *   report_viewer -> Report Viewer
 * @param {string} name - Role identifier (snake_case, kebab-case or spaced)
 * @returns {string} Human-readable label, '' for empty input
 */
export function formatRoleName(name) {
  if (!name) return '';

  return String(name)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(word => (
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ))
    .join(' ');
}

/**
 * Format a list of roles into a single display string.
 * Accepts either `['hr_admin']` or `[{ name: 'hr_admin' }]` - the API returns role
 * objects on some endpoints and bare strings on others.
 * @param {Array<string|{name: string}>} roles
 * @param {string} fallback - shown when there are no roles
 * @returns {string}
 */
export function formatRoleList(roles, fallback = '') {
  const names = (roles || [])
    .map(r => formatRoleName(typeof r === 'string' ? r : r?.name))
    .filter(Boolean);

  return names.length > 0 ? names.join(', ') : fallback;
}
